import type React from "react";
import { Link } from "react-router";
import { useAuth } from "@/app/auth-context";
import { usePermissions } from "@/hooks/use-permissions";
import { PageContainer } from "@/app/layouts/PageContainer";
import { Button } from "@/components/ui/button";
import { MyTasksWidget } from "./widgets/MyTasksWidget";
import { QuotesWidget } from "./widgets/QuotesWidget";
import { JobsWidget } from "./widgets/JobsWidget";
import { OverdueInvoicesWidget } from "./widgets/OverdueInvoicesWidget";
import { SalesTrendWidget } from "./widgets/SalesTrendWidget";

function greeting(d: Date): string {
  const h = d.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

type WidgetDef = { key: string; perm: string; Component: () => React.JSX.Element; span?: number };

const WIDGETS: readonly WidgetDef[] = [
  { key: "my-tasks", perm: "tasks:read",    Component: MyTasksWidget },
  { key: "quotes",   perm: "quotes:read",   Component: QuotesWidget },
  { key: "jobs",     perm: "jobs:read",     Component: JobsWidget },
  { key: "invoices", perm: "invoices:read", Component: OverdueInvoicesWidget },
  { key: "sales-trend", perm: "reporting:read", Component: SalesTrendWidget, span: 2 },
];

export function DashboardPage() {
  const { me } = useAuth();
  const { has } = usePermissions();
  const now = new Date();
  const visible = WIDGETS.filter((w) => has(w.perm));

  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">
            {greeting(now)}{me?.displayName ? `, ${me.displayName}` : ""}
          </h1>
          <p className="text-sm text-muted-foreground">
            {now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {has("quotes:write") && (
            <Button asChild>
              <Link to="/quotes/new">New Quote</Link>
            </Button>
          )}
          {has("tasks:read") && (
            <Button asChild variant="outline">
              <Link to="/tasks">Open Work Queue</Link>
            </Button>
          )}
          {has("statements:read") && (
            <Button asChild variant="outline">
              <Link to="/statements/runs">Bulk Statement Run</Link>
            </Button>
          )}
        </div>

        {visible.length === 0 ? (
          <p className="text-muted-foreground">Welcome. You don&#x2019;t have any dashboard widgets yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {visible.map((w) => (
              <div key={w.key} className={w.span === 2 ? "lg:col-span-2" : undefined}>
                <w.Component />
              </div>
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
